import React, { Suspense } from 'react';
import { Layout, Menu, Button, Space, message, Spin } from 'antd';
import { WalletOutlined } from '@ant-design/icons';
import logo from '../../assets/svg/maker-arkreen.svg';
import './Index.css';
import { loginWallet, checkLoginStatus, getWalletAddress, formatAddress, logout, WALLET_ADDRESS_KEY } from '../../utils/walletUtils';
import UserServiceApi from '../../api/userServiceApi';

const { Header, Content, Sider } = Layout;

// 懒加载组件
const PlantManage = React.lazy(() => import('../plant_manage/PlantManage'));
const UserManage = React.lazy(() => import('../system_setting/UserManage'));
const MinerManage = React.lazy(() => import('../plant_manage/MinerManage'));
const ApiPlantManage = React.lazy(() => import('../api_plant/ApiPlantManage'));
const PlugMiners = React.lazy(() => import('../socket_miner/PlugMiners'));
const OwonPlug = React.lazy(() => import('../socket_miner/OwonPlug'));
const ShinemonitorPlug = React.lazy(() => import('../socket_miner/ShinemonitorPlug'));
const LitePlantManage = React.lazy(() => import('../lite_manage/LitePlantManage'));
const LiteMinerManage = React.lazy(() => import('../lite_manage/LiteMinerManage'));
const LiteMinerAirdrop = React.lazy(() => import('../airdrop/LiteMinerAirdrop'));
const AKREAirdrop = React.lazy(() => import('../airdrop/AKREAirdrop'));
const ARTAirdrop = React.lazy(() => import('../airdrop/ARTAirdrop'));
const GatewayConfig = React.lazy(() => import('../gateway_config/GatewayConfig'));
const PlantMinerManage = React.lazy(() => import('../plant_miner/MinerManage'));
const EdrMiner = React.lazy(() => import('../plant_miner/EdrMiner'));
const StandardMinerManage = React.lazy(() => import('../standard_miner/StandardMinerManage'));

// 组件映射表
const componentMap = {
  'user-manage': UserManage,
  'miner-manage': MinerManage,
  'api-plant-manage': ApiPlantManage,
  'plant-manage': PlantManage,
  'plug-miners': PlugMiners,
  'owon-plug': OwonPlug,
  'shinemonitor-plug': ShinemonitorPlug,
  'lite-plant-manage': LitePlantManage,
  'lite-miner-manage': LiteMinerManage,
  'airdrop-lite-miner': LiteMinerAirdrop,
  'airdrop-akre': AKREAirdrop,
  'airdrop-art': ARTAirdrop,
  'gateway-config': GatewayConfig,
  'plant-miner-manage': PlantMinerManage,
  'plant-edr-miner': EdrMiner,
  'standard-miner-manage': StandardMinerManage,
};

// 菜单配置
const menuItems = [
  { key: 'dashboard', label: 'Dashboard' },
  {
    key: 'remote-miners',
    label: 'Remote Miners',
    children: [
      { key: 'plant-manage', label: 'Plant Manage' },
      { key: 'miner-manage', label: 'Miner Manage' },
      { key: 'miner-data', label: 'Miner Data' }
    ]
  },
  {
    key: 'lite-miners',
    label: 'Lite Miners',
    children: [
      { key: 'lite-plant-manage', label: 'Plant Manage' },
      { key: 'lite-miner-manage', label: 'Miner Manage' },
      { key: 'lite-miner-data', label: 'Miner Data' }
    ]
  },
  {
    key: 'standard-miner',
    label: 'Standard Miner',
    children: [
      { key: 'standard-miner-manage', label: 'Miner Manage' }
    ]
  },
  { key: 'coupon-setting', label: 'Coupon Setting' },
  {
    key: 'socket-miner',
    label: 'Socket Miner',
    children: [
      { key: 'plug-miners', label: 'Plug Miners' },
      { key: 'owon-plug', label: 'Owon Plug' },
      { key: 'shinemonitor-plug', label: 'Shinemonitor Plug' }
    ]
  },
  {
    key: 'plant-miner',
    label: 'Plant Miner',
    children: [
      { key: 'plant-miner-manage', label: 'Miner Manage' },
      { key: 'plant-edr-miner', label: 'Edr Miner' }
    ]
  },
  {
    key: 'system-setting',
    label: 'System Setting',
    children: [
      { key: 'user-manage', label: 'User Manage' }
    ]
  },
  {
    key: 'api-plant',
    label: 'Api Plant',
    children: [
      { key: 'api-plant-manage', label: 'Plant Manage' }
    ]
  },
  { key: 'preorder', label: 'Preorder' },
  {
    key: 'airdrop',
    label: 'Airdrop',
    children: [
      { key: 'airdrop-lite-miner', label: 'lite miner' },
      { key: 'airdrop-akre', label: 'akre' },
      { key: 'airdrop-art', label: 'ART' }
    ]
  },
  { key: 'csp-order', label: 'CSP Order' },
  { key: 'gateway-config', label: 'Gateway Config' }
];

// 默认占位组件
const DefaultPlaceholder = () => (
  <div style={{ textAlign: 'center', padding: '50px' }}>
    <h2>功能开发中...</h2>
    <p>该功能正在开发中，敬请期待</p>
  </div>
);

// 加载中组件
const LoadingSpinner = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', minHeight: 200 }}>
    <Spin size="large" />
  </div>
);

// 未登录页面组件
const LoginPage = ({ onLogin }) => (
  <Layout style={{ minHeight: '100vh' }}>
    <Header className="header">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div className="logo">
          <img src={logo} className="logo-svg" alt="logo" />
          <span className="logo-text">Arkreen Admin</span>
        </div>
        <div style={{ marginRight: '20px' }}>
          <Button type="primary" icon={<WalletOutlined />} onClick={onLogin}>
            连接钱包
          </Button>
        </div>
      </div>
    </Header>
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 64px)', background: '#f0f2f5' }}>
      <div style={{ textAlign: 'center' }}>
        <h2>请先连接钱包</h2>
        <p>连接MetaMask钱包以访问管理系统</p>
      </div>
    </div>
  </Layout>
);

class Index extends React.Component {
  state = {
    isLoggedIn: false,
    walletAddress: null,
    selectedKey: 'plant-manage'
  };

  componentDidMount() {
    this.checkLogin();
    if (window.ethereum) {
      window.ethereum.on('accountsChanged', this.handleAccountsChanged);
    }
  }

  componentWillUnmount() {
    if (window.ethereum) {
      window.ethereum.removeListener('accountsChanged', this.handleAccountsChanged);
    }
  }

  handleAccountsChanged = () => {
    this.handleLogout();
  };

  checkLogin = async () => {
    if (!checkLoginStatus()) {
      this.setState({ isLoggedIn: false, walletAddress: null });
      return;
    }

    const address = getWalletAddress();
    try {
      const tokenExist = await UserServiceApi.checkUserTokenExist({ address });
      const isValid = !tokenExist.error && tokenExist.result;
      this.setState({
        isLoggedIn: isValid,
        walletAddress: isValid ? address : null
      });
    } catch {
      this.setState({ isLoggedIn: false, walletAddress: null });
    }
  };

  handleLogin = async () => {
    const loginData = await loginWallet();
    if (loginData) {
      localStorage.setItem(WALLET_ADDRESS_KEY, loginData.address);
      message.success('钱包登录成功');
      this.checkLogin();
    }
  };

  handleLogout = () => {
    logout();
    this.setState({ isLoggedIn: false, walletAddress: null });
    message.success('已登出');
  };

  handleMenuSelect = ({ key }) => {
    this.setState({ selectedKey: key });
  };

  renderContent = () => {
    const Component = componentMap[this.state.selectedKey];
    return Component ? <Component /> : <DefaultPlaceholder />;
  };

  render() {
    const { isLoggedIn, walletAddress, selectedKey } = this.state;

    if (!isLoggedIn) {
      return <LoginPage onLogin={this.handleLogin} />;
    }

    return (
      <Layout style={{ minHeight: '100vh' }}>
        <Header className="header" style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div className="logo">
              <img src={logo} className="logo-svg" alt="logo" />
              <span className="logo-text">Arkreen Admin</span>
            </div>
            <div style={{ marginRight: '20px' }}>
              <Space>
                <span style={{ color: '#fff' }}>{formatAddress(walletAddress)}</span>
                <Button type="link" onClick={this.handleLogout}>登出</Button>
              </Space>
            </div>
          </div>
        </Header>
        <Layout style={{ marginTop: 64 }}>
          <Sider width={200} style={{ background: '#001529', height: 'calc(100vh - 64px)', position: 'fixed', left: 0, top: 64, overflowY: 'auto', overflowX: 'hidden' }}>
            <Menu
              theme="dark"
              mode="inline"
              selectedKeys={[selectedKey]}
              style={{ borderRight: 0 }}
              items={menuItems}
              onSelect={this.handleMenuSelect}
            />
          </Sider>
          <Layout style={{ marginLeft: 200, minHeight: 'calc(100vh - 64px)' }}>
            <Content style={{ padding: 24, margin: 0, background: '#FFFFFF', overflow: 'auto' }}>
              <Suspense fallback={<LoadingSpinner />}>
                {this.renderContent()}
              </Suspense>
            </Content>
          </Layout>
        </Layout>
      </Layout>
    );
  }
}

export default Index;
